import manualService from '@/services/manualService';
import { DateTime } from 'luxon';
import { kebabCase } from 'lodash';

const tutorials = {
  tutorials: [
    {
      id: 'displayAndControls',
      title: 'Display and controls',
      description: '',
      order: 0,
      models: ['a04', 'a05', 'a06', 'a22', 'a36'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            chronoZero: {
              hours: 0,
              minutes: 20,
              seconds: 0,
              milliseconds: 0,
            },
            chrono: {
              isRunning: true,
            },
          },
          instruction: {
            model: 'a04',
            get title() {
              return `Test drive the ${kebabCase(this.model).toUpperCase()}`;
            },
            get text(){
              return `<p>Let's get you introduced to the ${kebabCase(this.model).toUpperCase()}'s parts.</p>
                      <p>Click <b>"Begin"</b> to continue.</p>`
            }
          },
        },
        {
          order: 1,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a04',
                get id() {
                  return `Hour_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'hour',
              },
            ],
          },
          instruction: {
            model: 'a04',
            title: 'Hour hand',
            get text() {
              return `<p>This is the <b>Hour hand</b>, showing the current hour</p>`;
            },
          },
        },
        {
          order: 2,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a04',
                get id() {
                  return `Minute_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'minute',
              },
            ],
          },
          instruction: {
            model: 'a04',
            title: 'Minute hand',
            get text() {
              return `<p>This is the <b>Minute hand</b>, showing the current minute.</p>`;
            },
          },
        },
        {
          order: 3,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a04',
                get id() {
                  const componentName = (this.model === 'a22' || this.model === 'a36') ? 'Small_Second' : 'Second';
                  return `${componentName}_${kebabCase(this.model).toUpperCase()}`;
                },
                get role() {
                  return (this.model === 'a22' || this.model === 'a36')
                    ? 'smallSecond'
                    : 'second';
                },
              },
            ],
          },
          instruction: {
            model: 'a04',
            title: 'Second hand',
            get text() {
              return `<p>This is the <b>Second hand</b>, showing the current second.</p>`;
            },
          },
        },
        {
          order: 4,
          type: 'intro',
          for: ['a22', 'a36'],
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a22',
                get id() {
                  return `Chrono_Hour_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'chronoH',
              },
            ],
          },
          instruction: {
            model: 'a22',
            get title() {
              return this.model === 'a22' ? 'Hour counter' : 'Tenth-second/Hour counter';
            },
            get text() {
              if (this.model === 'a22') {
                return `<p>This is the <b>Hour counter</b>, used by chronograph functions to display 
                        the number of elapsed hours.</p>`;
              }
              return `<p>This is the <b>Tenth-second/Hour counter</b>.</p>
                        <p>It's used by chronograph functions to display the current tenth-second value 
                        during the first thirty minutes of measurement. Beyond thirty minutes, it 
                        displays the number of elapsed hours.</p>`;
            },
          },
        },
        {
          order: 5,
          type: 'intro',
          for: ['a22', 'a36'],
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a22',
                get id() {
                  return `Chrono_Minute_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'chronoM',
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Minute counter',
            get text() {
              return `<p>This is the <b>Minute counter</b>, used by chronograph functions to display 
                      the number of elapsed minutes.</p>`;
            },
          },
        },
        {
          order: 6,
          type: 'intro',
          for: ['a22', 'a36'],
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a22',
                get id() {
                  return `Second_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'second',
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Second counter',
            get text() {
              return `<p>This is the <b>Second counter</b>, used by chronograph functions to display
                      the number of elapsed seconds.</p>`;
            },
          },
        },
        {
          order: 7,
          type: 'intro',
          for: ['a05', 'a06', 'a22', 'a36'],
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a05',
                get id() {
                  return `Day-ring_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'day',
              },
            ],
          },
          instruction: {
            model: 'a05',
            title: 'Day indicator',
            get text() {
              return `<p>This is the <b>Day indicator</b>, which displays the current date.</p>`;
            },
          },
        },
        {
          order: 8,
          type: 'intro',
          for: ['a06'],
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a06',
                get id() {
                  return `Weekday-ring_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'weekday',
              },
            ],
          },
          instruction: {
            model: 'a05',
            title: 'Weekday indicator',
            get text() {
              return `<p>This is the <b>Weekday indicator</b>, which displays the current weekday.</p>`;
            },
          },
        },
        {
          order: 9,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a04',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'crown',
              },
            ],
          },
          instruction: {
            model: 'a04',
            title: 'Crown',
            get text() {
              let crownFunctions = '';
              switch (this.model) {
                case 'a04': crownFunctions = 'the hands';
                  break;
                case 'a06': crownFunctions = 'the hands, day and weekday';
                  break;
                default: crownFunctions = 'the hands and day';
              }
              const crownFunctionsLength = getModel(this.model).components.crown.functions.length;
              return `<p>This is the <b>Crown</b>, which unscrews from the watch, has 
                      ${crownFunctionsLength} positions, and is used to set ${crownFunctions}.</p>
                      <p>When you're not using the crown, be sure it's securely
                      screwed in: your watch's water resistance depends on a tight seal.</p>`;
            },
          },
        },
        {
          order: 10,
          type: 'intro',
          for: ['a22', 'a36'],
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'pusherA',
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Pusher A',
            get text() {
              return `<p>This is <b>Pusher A</b>, which when pressed is used to start,
              pause and otherwise control chronograph functions.</p>`;
            },
          },
        },
        {
          order: 11,
          type: 'intro',
          for: ['a22', 'a36'],
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            displayElements: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                role: 'pusherB',
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Pusher B',
            get text() {
              return `<p>This is <b>Pusher B</b>, which when pressed is used to reset
              and otherwise control chronograph functions.</p>`;
            },
          },
        },
        {
          order: 12,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            chrono: {
              isRunning: false,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a04',
            title: 'Complete',
            get text() {
              return `<p>Now that you know the parts of your watch, let's move on to how it works.</p>
                      <p>Click the button labeled, <b>"Next: Setting the time,"</b> to continue 
                      to the next section.</p>`;
            },
          },
        },
      ],
    },
    {
      id: 'setTime',
      title: 'Setting the time',
      description: '',
      order: 1,
      models: ['a04', 'a05', 'a06', 'a22', 'a36'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            chrono: {
              isRunning: false,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a04',
            title: 'Setting the time',
            get text() {
              return `<p>Here we'll learn how to <b>set the time</b>.</p>
                      <p>Click <b>"Begin"</b> to continue.</p>`;
            },
          },
        },
        {
          order: 0,
          type: 'step',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            components: [
              {
                model: 'a04',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            model: 'a04',
            get crownPosition() {
              return getCrownPosition(this.model, 'setTime');
            },
            get crownClicksText() {
              return getCrownClicksCount(this.model, 'setTime');
            },
            title: 'Pull crown out',
            get text() {
              return `<p>Unscrew and gently pull out the <b>Crown</b> to its ${this.crownPosition} position.
                            <span v-if="this.crownClicksText">(${this.crownClicksText})</span>
                          </p>
                          <p>Click or tap on the crown to pull it out.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a04',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setTime').position;
                },
              },
            ],
          },
        },
        {
          order: 1,
          type: 'step',
          init: {
            time: {
              isRunning: false,
              get value() {
                return DateTime.local().minus({ days: 3, hours: 3 });
              },
            },
            components: [
              {
                model: 'a04',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setTime').position;
                },
              },
              {
                model: 'a04',
                get id() {
                  return `Arrow_clockwise_${kebabCase(this.model).toUpperCase()}`;
                },
              },
              {
                model: 'a04',
                get id() {
                  return `Arrow_counterclockwise_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
          },
          instruction: {
            title: 'Set the hands',
            model: 'a04',
            get crownPosition() {
              return getCrownPosition(this.model, 'setTime');
            },
            get targetTime() {
              return DateTime.local().minus({ days: 3 }).toLocaleString(DateTime.TIME_SIMPLE);
            },
            get text() {
              return `<p>In this ${this.crownPosition} crown position, the watch stops running.</p>
                      <p><b>Turn the crown clockwise</b> to advance the watch hands three (3) hours to the current time:</p>
                      <h2 class="tutorial-target">${this.targetTime}</h2>
                      <p>Note: A clockwise turn advances the hands, while a counterclockwise turn
                         reverses the hands. (Hold down either button to more quickly advance the hands.)</p>`;
            },
          },
          expectation: {
            // TODO: Should the target time be relative to "NOW"? (e.g. "advance the hands 6 hours")
            // Will require access to the model's this.model.now object (in the case hands have already been adjusted)
            // Which in turn will require the expectation's 'model' to be set before 'value' is used...
            time: {
              model: 'a05',
              get value() {
                return DateTime.local().minus({ days: 3 });
              },
              margin: {
                minutes: 2,
              },
            },
          },
        },
        {
          order: 2,
          type: 'step',
          init: {
            time: {
              isRunning: false,
              get value() {
                return DateTime.local().minus({ days: 3 });
              },
            },
            components: [
              {
                model: 'a04',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 1,
              },
            ],
          },
          instruction: {
            title: 'Push in crown',
            get text() {
              return `<p>Return the crown to its original, screwed in position.</p>
                      <p>Click or tap on the crown to return it to its first position.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a04',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
        },
        {
          order: 3,
          type: 'complete',
          init: {
            time: {
              isRunning: true,
              get value() {
                return DateTime.local().minus({ days: 3 });
              },
            },
            components: [
              {
                model: 'a04',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            title: 'Success!',
            get text() {
              return `<p>Great! You've successfully set the time.</p>
                      <p>The watch has started running again.</p>`;
            },
          },
          expectation: {},
        },
      ],
    },
    {
      id: 'setDay',
      title: 'Setting the day',
      description: '',
      order: 2,
      models: ['a05', 'a06', 'a22', 'a36'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
              // get value() {
              //   return DateTime.local().minus({ days: 3 });
              // },
            },
            day: {
              model: 'a05',
              get value() {
                return DateTime.local().minus({ days: 3 }).day;
              },
            },
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            chrono: {
              isRunning: false,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a05',
            title: 'Setting the day',
            text: `<p>Here we'll learn how to set the <b>Day indicator</b>.</p>
                   <p>Click <b>"Begin"</b> to continue.</p>`,
          },
        },
        {
          order: 0,
          type: 'step',
          init: {
            time: {
              isRunning: true
            },
            day: {
              model: 'a05',
              get value() {
                return DateTime.local().minus({ days: 3 }).day;
              },
            },
            components: [
              {
                model: 'a05',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            model: 'a05',
            get crownPosition() {
              return getCrownPosition(this.model, 'setDay');
            },
            get crownClicksText() {
              return getCrownClicksCount(this.model, 'setDay');
            },
            title: 'Pull crown out',
            get text() {
              return `<p>Unscrew and gently pull out the crown to its ${this.crownPosition} position.
                            <span v-if="this.crownClicksText">(${this.crownClicksText})</span>
                          </p>
                          <p>Click or tap on the crown to pull it out.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a05',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setDay').position;
                },
              },
            ],
          },
        },
        {
          order: 1,
          type: 'step',
          init: {
            time: {
              isRunning: true
            },
            day: {
              model: 'a05',
              get value() {
                return DateTime.local().minus({ days: 3 }).day;
              },
            },
            components: [
              {
                model: 'a05',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setDay').position;
                },
              },
              {
                model: 'a05',
                get id() {
                  return `Arrow_clockwise_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
          },
          instruction: {
            model: 'a05',
            get crownPosition() {
              return getCrownPosition(this.model, 'setDay');
            },
            get crownClicksText() {
              return getCrownClicksCount(this.model, 'setDay');
            },
            title: 'Adjust day',
            get text() {
              return `<p>Advance the day display to today's date:</p>
                      <h2 class="tutorial-target">${DateTime.local().toFormat('MMMM dd')}</h2>
                      <p>Tap or click the clockwise arrow to advance the day display.</p>`;
            },
          },
          expectation: {
            day: {
              model: 'a05',
              get value() {
                return DateTime.local().day;
              },
              margin: {
                days: 1
              },
            },
            components: [
              {
                model: 'a05',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setDay').position;
                },
              },
            ],
          },
        },
        {
          order: 2,
          type: 'step',
          init: {
            time: {
              isRunning: true
            },
            day: {
              model: 'a05',
              get value() {
                return DateTime.local().day;
              },
            },
            components: [
              {
                model: 'a05',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setDay').position;
                },
              },
            ],
          },
          instruction: {
            title: 'Push in crown',
            get text() {
              return `<p>Return the crown to its original, screwed in position.</p>
                      <p>Click or tap on the crown until it returns to its first position.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a05',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
        },
        {
          order: 3,
          type: 'complete',
          init: {
            time: {
              isRunning: true
            },
            day: {
              model: 'a05',
              get value() {
                return DateTime.local().day;
              },
            },
            components: [
              {
                model: 'a05',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            title: 'Success!',
            get text() {
              return `<p>Great! You've successfully set the day display.</p>
                      <p>The watch has started running again.</p>`;
            },
          },
          expectation: {},
        },
      ],
    },
    {
      id: 'setWeekday',
      title: 'Setting the weekday',
      description: '',
      order: 3,
      models: ['a06'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true
            },
            weekday: {
              model: 'a06',
              get value() {
                return DateTime.local().minus({ days: 3 }).weekday;
              },
            },
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            chrono: {
              isRunning: false,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a06',
            title: 'Setting the weekday',
            text: `<p>Here we'll learn how to set the <b>Weekday indicator</b>.</p>
                   <p>Click <b>"Begin"</b> to continue.</p>`,
          },
        },
        {
          order: 0,
          type: 'step',
          init: {
            time: {
              isRunning: true
            },
            weekday: {
              model: 'a06',
              get value() {
                return DateTime.local().minus({ days: 3 }).weekday;
              },
            },
            components: [
              {
                model: 'a06',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            model: 'a06',
            get crownPosition() {
              return getCrownPosition(this.model, 'setWeekday');
            },
            get crownClicksText() {
              return getCrownClicksCount(this.model, 'setWeekday');
            },
            title: 'Pull crown out',
            get text() {
              return `<p>Unscrew and gently pull out the crown to its ${this.crownPosition} position.
                            <span v-if="this.crownClicksText">(${this.crownClicksText})</span>
                          </p>
                          <p>Click or tap on the crown to pull it out.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a06',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setWeekday').position;
                },
              },
            ],
          },
        },
        {
          order: 1,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            weekday: {
              model: 'a06',
              get value() {
                return DateTime.local().minus({ days: 3 }).weekday;
              },
            },
            components: [
              {
                model: 'a06',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setWeekday').position;
                },
              },
              {
                model: 'a06',
                get id() {
                  return `Arrow_counterclockwise_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
          },
          instruction: {
            model: 'a06',
            get crownPosition() {
              return getCrownPosition(this.model, 'setWeekday');
            },
            get crownClicksText() {
              return getCrownClicksCount(this.model, 'setWeekday');
            },
            title: 'Adjust weekday',
            get text() {
              return `<p>Advance the weekday display to show today's weekday:</p>
                      <h2 class="tutorial-target">${DateTime.local().toFormat('cccc')}</h2>
                      <p>Tap or click the counterclockwise arrow to advance the weekday display.</p>`;
            },
          },
          expectation: {
            weekday: {
              model: 'a06',
              get value() {
                return DateTime.local().weekday;
              },
              margin: {
                days: 1
              },
            },
            components: [
              {
                model: 'a06',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setWeekday').position;
                },
              },
            ],
          },
        },
        {
          order: 2,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            weekday: {
              model: 'a06',
              get value() {
                return DateTime.local().weekday;
              },
            },
            components: [
              {
                model: 'a06',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'setWeekday').position;
                },
              },
            ],
          },
          instruction: {
            title: 'Push in crown',
            get text() {
              return `<p>Return the crown to its original, screwed in position.</p>
                      <p>Click or tap on the crown until it returns to its first position.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a06',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
        },
        {
          order: 3,
          type: 'complete',
          init: {
            time: {
              isRunning: true,
            },
            weekday: {
              model: 'a06',
              get value() {
                return DateTime.local().weekday;
              },
            },
            components: [
              {
                model: 'a06',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            title: 'Success!',
            get text() {
              return `<p>Great! You've successfully set the weekday display.</p>
                      <p>The watch has started running again.</p>`;
            },
          },
          expectation: {},
        },
      ],
    },
    {
      id: 'chronoBasicTiming',
      title: 'Basic timing',
      description: '',
      order: 4,
      models: ['a22', 'a36'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
            },
            chrono: {
              isRunning: false,
            },
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a22',
            title: 'Basic timing',
            get text() {
              return `<p>Here we'll learn how to use the chronograph's <b>Timing function</b>.</p>
                      <p>Click <b>"Begin"</b> to continue.</p>`;
            },
          },
        },
        {
          order: 0,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return manualService.models
                    .find(m => m.id === this.model).components.pusherA.value;
                },
              },
            ],
          },
          instruction: {
            title: 'Start timing',
            get text() {
              return `<p>Press <b>Pusher A</b> to start the chronograph's timing function.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 2,
              },
            ],
          },
        },
        {
          order: 1,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 2,
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Pause timing',
            get modelElem() {
              return manualService.models.find(m => m.id === this.model);
            },
            get chronoRunning() {
              return this.modelElem.chronoRunning;
            },
            get chronoNow() {
              if (this.modelElem.chronoNow) {
                return this.model === 'a36'
                  ? this.modelElem.chronoNow.toFormat('m \' minutes, \' s \' seconds, and \' SSS \' milliseconds\'')
                  : this.modelElem.chronoNow.toFormat('m \' minutes and \' s \' seconds');
              }
            },
            get text() {
              return `<div class="tutorial-target">
                        <h5>${this.chronoRunning ? 'Running' : 'Paused'}</h5>
                        <p>${this.chronoNow ? this.chronoNow : 'Chronograph is untouched.'}</p>
                      </div>
                      <p>Press <b>Pusher A</b> again to pause the chronograph's timing function.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 1,
              },
            ],
          },
        },
        {
          order: 2,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 1,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Reset counters',
            get modelElem() {
              return manualService.models.find(m => m.id === this.model);
            },
            get chronoRunning() {
              return this.modelElem.chronoRunning;
            },
            get chronoNow() {
              if (this.modelElem.chronoNow) {
                return this.model === 'a36'
                  ? this.modelElem.chronoNow.toFormat('m \' minutes, \' s \' seconds, and \' SSS \' milliseconds\'')
                  : this.modelElem.chronoNow.toFormat('m \' minutes and \' s \' seconds');
              }
            },
            get text() {
              return `<div class="tutorial-target">
                        <h5>${this.chronoRunning ? 'Running' : 'Paused'}</h5>
                        <p>${this.chronoNow ? this.chronoNow : 'Chronograph is untouched.'}</p>
                      </div>
                      <p>To resume and pause timing, continue pressing <b>Pusher A</b>.<br>
                      (Resume; Pause; Resume; etc.)</p>
                      <p>To reset the timing function, press <b>Pusher B</b> when timing is paused.
                      The chronograph counters will return to their zero positions.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
        },
        {
          order: 3,
          type: 'complete',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            title: 'Success!',
            get text() {
              return `<p>You've successfully used the chronograph's basic timing function.</p>`;
            },
          },
          expectation: {},
        },
      ],
    },
    {
      id: 'chronoIntervalTiming',
      title: 'Interval timing',
      description: '',
      order: 5,
      models: ['a22', 'a36'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
            },
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            chrono: {
              isRunning: false,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a22',
            title: 'Interval timing',
            get text() {
              return `<p>Here we'll learn how to use the chronograph's <b>Interval function</b>.</p>
                      <p>Click <b>"Begin"</b> to continue.</p>`;
            },
          },
        },
        {
          order: 0,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return manualService.models
                    .find(m => m.id === this.model).components.pusherA.value;
                },
              },
            ],
          },
          instruction: {
            title: 'Start timing',
            get text() {
              return `<p>Press <b>Pusher A</b> to start the chronograph's timing function.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 2,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
        },
        {
          order: 1,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return manualService.models
                    .find(m => m.id === this.model).components.pusherB.value;
                },
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Display interval',
            get modelElem() {
              return manualService.models.find(m => m.id === this.model);
            },
            get chronoRunning() {
              return this.modelElem.chronoRunning;
            },
            get chronoNow() {
              if (this.modelElem.chronoNow) {
                return this.model === 'a36'
                  ? this.modelElem.chronoNow.toFormat('m \' minutes, \' s \' seconds, and \' SSS \' milliseconds\'')
                  : this.modelElem.chronoNow.toFormat('m \' minutes and \' s \' seconds');
              }
            },
            get text() {
              return `<p>Press <b>Pusher B</b> to show the elapsed interval.</p>
                      <div class="tutorial-target">
                        <h5>${this.chronoInterval ? 'Interval displayed' : 'Interval'}</h5>
                        <p>${this.chronoInterval ? this.chronoInterval : 'Not set'}</p>
                      </div>
                      <div class="tutorial-target">
                        <h5>${this.chronoNow && !this.chronoInterval ? 'Timing displayed' : 'Timing'}</h5>
                        <p>${this.chronoNow ? this.chronoNow : 'Chronograph isn\'t running.'}</p>
                      </div>
                      <p>(Timing continues behind the scenes.)</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 3,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 2,
              },
            ],
          },
        },
        {
          order: 2,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return manualService.models
                    .find(m => m.id === this.model).components.pusherA.value;
                },
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return manualService.models
                    .find(m => m.id === this.model).components.pusherB.value;
                },
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Toggle interval',
            get modelElem() {
              return manualService.models.find(m => m.id === this.model);
            },
            get chronoInterval() {
              if (this.modelElem.chronoInterval) {
                return this.model === 'a36'
                  ? this.modelElem.chronoInterval.toFormat('m \' minutes, \' s \' seconds, and \' SSS \' milliseconds\'')
                  : this.modelElem.chronoInterval.toFormat('m \' minutes and \' s \' seconds');
              }
            },
            get chronoNow() {
              if (this.modelElem.chronoNow) {
                return this.model === 'a36'
                  ? this.modelElem.chronoNow.toFormat('m \' minutes, \' s \' seconds, and \' SSS \' milliseconds\'')
                  : this.modelElem.chronoNow.toFormat('m \' minutes and \' s \' seconds');
              }
            },
            get text() {
              return `<p>Continue pressing <b>Pusher B</b> to switch between interval and timing display.
                      (Counters skip ahead to the current chronograph timing.)</p>
                      <div class="tutorial-target">
                        <h5>${this.chronoInterval ? 'Interval displayed' : 'Interval'}</h5>
                        <p>${this.chronoInterval ? this.chronoInterval : 'Not set'}</p>
                      </div>
                      <div class="tutorial-target">
                        <h5>${this.chronoNow && !this.chronoInterval ? 'Timing displayed' : 'Timing'}</h5>
                        <p>${this.chronoNow ? this.chronoNow : 'Chronograph isn\'t running.'}</p>
                      </div>
                      <p>Finally, pause timing by pressing <b>Pusher A</b> when
                      an interval isn't set (i.e. when chronograph timing is displayed).</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 1,
              },
            ],
          },
        },
        {
          order: 3,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return manualService.models
                    .find(m => m.id === this.model).components.pusherB.value;
                },
              },
            ],
          },
          instruction: {
            title: 'Reset counters',
            get text() {
              return `<p>Press <b>Pusher B</b> to reset the chronograph counter hands to zero.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
        },
        {
          order: 4,
          type: 'complete',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            title: 'Success!',
            get text() {
              return `<p>You've successfully used the chronograph's interval function.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
        },
      ],
    },
    {
      id: 'resetCountersA22',
      title: 'Reset counter positions',
      description: '',
      order: 6,
      models: ['a22'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
            chrono: {
              isRunning: false,
            },
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 1,
              milliseconds: 0,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a22',
            title: 'Reset counter positions',
            get text() {
              return `<p>Take a look at those counters. None of them are lined up to zero! 
                      Let's learn how to fix that problem.</p>
                      <p>Click <b>"Begin"</b> to continue.</p>`;
            },
          },
        },
        {
          order: 0,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 1,
              milliseconds: 0,
            },
          },
          instruction: {
            model: 'a22',
            get crownPosition() {
              return getCrownPosition(this.model, 'resetCounters');
            },
            get crownClicksText() {
              return getCrownClicksCount(this.model, 'resetCounters');
            },
            title: 'Pull crown out',
            get text() {
              return `<p>Unscrew and gently pull out the <b>Crown</b> to its ${this.crownPosition} position.
                            <span v-if="this.crownClicksText">(${this.crownClicksText})</span>
                          </p>
                          <p>Click or tap on the crown to pull it out.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'resetCounters').position;
                },
              },
            ],
          },
        },
        {
          order: 1,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 1,
              milliseconds: 0,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            model: 'a22',
            title: 'Activate counter reset function',
            get text() {
              return `<p>Press and hold <b>Pusher A</b> <i>and</i> <b>Pusher B</b> for three seconds.</p>
                      <p>The chronograph counters will rotate to their zero positions once activated.</p>`;
            },
          },
          expectation: {
            counterReset: {
              inProgress: true,
              value: 1,
            },
          },
        },
        {
          order: 2,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 1,
              milliseconds: 0,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            counterReset: {
              value: 1,
            },
          },
          instruction: {
            model: 'a22',
            title: 'Reset second counter',
            // get hourCounterName () {
            //   return this.model === 'a22' ? 'hour' : 'tenth-second/hour'
            // },
            get text() {
              return `<p>The counter reset function has been activated!</p>
                      <p>First to be adjusted is the second counter.
                      Press or hold <b>Pusher A</b> to advance it to its corrected zero position.</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 1,
            },
          },
        },
        {
          order: 3,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 0,
              milliseconds: 0,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            counterReset: {
              inProgress: true,
              value: 1,
            },
          },
          instruction: {
            model: 'a22',
            title: 'Advance to hour/minute counters',
            get text() {
              return `<p>Great! You've set the second counter to zero.
                      Press <b>Pusher B</b> to advance to the hour and minute counters.</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 2,
            },
          },
        },
        {
          order: 4,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            chronoZero: {
              hours: 7,
              minutes: 24,
              seconds: 0,
              milliseconds: 0,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            counterReset: {
              inProgress: true,
              value: 2,
            },
          },
          instruction: {
            model: 'a22',
            title: 'Reset hour and minute counters',
            get text() {
              return `<p>As before, press or hold <b>Pusher A</b> to advance both hour and minute counters 
                      to their correct zero position.</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 2,
            },
          },
        },
        {
          order: 5,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            components: [
              {
                model: 'a22',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'resetCounters').position;
                },
              },
            ],
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 2,
            },
          },
          instruction: {
            model: 'a22',
            title: 'Push in crown',
            get text() {
              return `<p>Great! You've reset all the chronograph's counters to zero.</p>
                      <p>(Note: If at this point you notice any counters aren't correctly set to zero,
                      you would press <b>Pusher B</b> to restart the counter reset process.)</p>
                      <p>Now that you're done with corrections, return the <b>Crown</b> back to its 
                      original, screwed in position.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a22',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: false,
              value: 0,
            },
          },
        },
        {
          order: 5,
          type: 'complete',
          init: {
            time: {
              isRunning: true,
            },
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: false,
              value: 0,
            },
          },
          instruction: {
            model: 'a22',
            title: 'Success!',
            get text() {
              return `<p>You reset the chronograph's counters to their correct zero positions. Nice work!</p>
                      <p>The watch is once again running.</p>`;
            },
          },
          expectation: {},
        },
      ],
    },
    {
      id: 'resetCountersA36',
      title: 'Reset counter positions',
      description: '',
      order: 7,
      models: ['a36'],
      steps: [
        {
          order: 0,
          type: 'intro',
          init: {
            time: {
              isRunning: true,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
            chrono: {
              isRunning: false,
            },
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 1,
              milliseconds: 300,
            },
            displayElements: [],
          },
          instruction: {
            model: 'a36',
            title: 'Reset counter positions',
            get text() {
              return `<p>Take a look at those counters. None of them are lined up to zero! 
                      Let's learn how to fix that problem.</p>
                      <p>Click <b>"Begin"</b> to continue.</p>`;
            },
          },
        },
        {
          order: 0,
          type: 'step',
          init: {
            time: {
              isRunning: true,
            },
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 1,
              milliseconds: 300,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            model: 'a36',
            get crownPosition() {
              return getCrownPosition(this.model, 'resetCounters');
            },
            get crownClicksText() {
              return getCrownClicksCount(this.model, 'resetCounters');
            },
            title: 'Pull crown out',
            get text() {
              return `<p>Unscrew and gently pull out the <b>crown</b> to its ${this.crownPosition} position.
                            <span v-if="this.crownClicksText">(${this.crownClicksText})</span>
                          </p>
                          <p>Click or tap on the crown to pull it out.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a36',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'resetCounters').position;
                },
              },
            ],
          },
        },
        {
          order: 1,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 1,
              milliseconds: 300,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
              {
                model: 'a36',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
          },
          instruction: {
            model: 'a36',
            title: 'Activate counter reset function',
            get text() {
              return `<p>Press and hold pushers A <i>and</i> B for three seconds.</p>
                      <p>The chronograph counters will rotate to their zero positions once activated.</p>`;
            },
          },
          expectation: {
            counterReset: {
              inProgress: true,
              value: 1,
            },
          },
        },
        {
          order: 2,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 1,
              milliseconds: 300,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            counterReset: {
              inProgress: true,
              value: 1,
            },
          },
          instruction: {
            model: 'a36',
            title: 'Set second counter to zero',
            get text() {
              return `<p>The counter reset function has been activated!</p>
                      <p>First to be adjusted is the second counter.
                      Press or hold <b>Pusher A</b> to advance it to its corrected zero position.</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 0,
              // minutes: 4,
              seconds: 0,
              // milliseconds: 300
            },
            counterReset: {
              inProgress: true,
              value: 1,
            },
          },
        },
        {
          order: 3,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 0,
              milliseconds: 300,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            counterReset: {
              inProgress: true,
              value: 1,
            },
          },
          instruction: {
            model: 'a36',
            title: 'Proceed to tenth-second/hour counter',
            get text() {
              return `<p>Great! You've set the second counter to zero.
                      Press <b>Pusher B</b> to proceed to the tenth-second/hour counter.</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 0,
              // minutes: 4,
              seconds: 0,
              // milliseconds: 300
            },
            counterReset: {
              inProgress: true,
              value: 3,
            },
          },
        },
        {
          order: 4,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 0,
              milliseconds: 300,
            },
            counterReset: {
              inProgress: true,
              value: 3,
            },
          },
          instruction: {
            model: 'a36',
            title: 'Set tenth-second/hour counter to zero',
            get text() {
              return `<p>As before, press or hold <b>Pusher A</b> to advance the tenth-second/hour counter 
                      to its correct zero position.</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 0,
              // minutes: 4,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 3,
            },
          },
        },
        {
          order: 5,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Pusher-B_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 3,
            },
          },
          instruction: {
            model: 'a36',
            title: 'Proceed to minute counter',
            get text() {
              return `<p>Great! You've set the tenth-second/hour counter to zero.
                      Press <b>Pusher B</b> to proceed to the minute counter. (Almost there!)</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 4,
            },
          },
        },
        {
          order: 6,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Pusher-A_${kebabCase(this.model).toUpperCase()}`;
                },
              },
            ],
            chronoZero: {
              hours: 0,
              minutes: 4,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 4,
            },
          },
          instruction: {
            model: 'a36',
            title: 'Set minute counter to zero',
            get text() {
              return `<p>Once more, press or hold <b>Pusher A</b> to advance the minute counter 
                      to its correct zero position.</p>`;
            },
          },
          expectation: {
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 4,
            },
          },
        },
        {
          order: 7,
          type: 'step',
          init: {
            time: {
              isRunning: false,
            },
            components: [
              {
                model: 'a36',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                get value() {
                  return getOpt(this.model, 'crown', 'resetCounters').position;
                },
              },
            ],
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: true,
              value: 4,
            },
          },
          instruction: {
            model: 'a36',
            title: 'Push in crown',
            get text() {
              return `<p>Great! You've reset all the chronograph's counters to zero.</p>
                      <p>(Note: If at this point you notice any counters aren't correctly set to zero,
                      you would press <b>Pusher B</b> to restart the counter reset process.)</p>
                      <p>Now that you're done with corrections, return the <b>Crown</b> back to its 
                      original, screwed in position.</p>`;
            },
          },
          expectation: {
            components: [
              {
                model: 'a36',
                get id() {
                  return `Crown_${kebabCase(this.model).toUpperCase()}`;
                },
                value: 0,
              },
            ],
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: false,
              value: 0,
            },
          },
        },
        {
          order: 8,
          type: 'complete',
          init: {
            time: {
              isRunning: true,
            },
            chronoZero: {
              hours: 0,
              minutes: 0,
              seconds: 0,
              milliseconds: 0,
            },
            counterReset: {
              inProgress: false,
              value: 0,
            },
          },
          instruction: {
            model: 'a36',
            title: 'Success!',
            get text() {
              return `<p>You reset the chronograph's counters to their correct zero positions. Nice work!</p>
                      <p>The watch is once again running.</p>`;
            },
          },
          expectation: {},
        },
      ],
    },
  ],
};

function getModel(modelId) {
  return manualService.models.find(m => m.id === modelId);
}
function getOpt(modelId, componentName, typeId) {
  return getModel(modelId).components[componentName].functions
    .find(f => f.options.find(opt => opt.type === typeId));
}
function getCrownPosition(modelId, typeId) {
  const crown = getOpt(modelId, 'crown', typeId);
  return crown.position ? pluralizeCrownPosition(crown.position) : undefined;
}
function pluralizeCrownPosition(position) {
  switch (position) {
    case 0: return 'first';
    case 1: return 'second';
    case 2: return 'third';
  }
}
function pluralizeCrownClicks(position) {
  switch (position) {
    case 1: return 'You should feel the movement click once.';
    case 2: return 'You should feel the movement click twice.';
  }
}
function getCrownClicksCount(modelId, typeId) {
  const crown = getOpt(modelId, 'crown', typeId);
  return crown.position ? pluralizeCrownClicks(crown.position) : undefined;
}

export default tutorials;
